/**
 * batch-68 werkplaatsfacturen (FO-341) — gedeelde types, labels en Zod-schema's.
 *
 *   - afzenderprofielen: mail-afzender -> leverancier (adres of domein);
 *   - de trefwoordenlijst per garage (sectieclassificatie vóór de AI);
 *   - het onderhoudsinterval per merk (+ model) en per voertuig (override);
 *   - het terugdraaien van een registratie (rollback van de intervalreset);
 *   - de onderhoudsbadge (laatste beurt, volgende beurt, APK met RDW-status);
 *   - de herkenning van de leverancier en de vaste termijn in de inkoop-review.
 *
 * De bestaande FO-329-typen (analyse, werkvoorraad, backfill) staan in
 * `garage-onderhoud.ts`; de reminders/intervallen per soort in `garage-voertuig.ts`.
 * Geen leveranciersnamen: alles is beheer-data.
 */

import { z } from 'zod'
import {
  onderhoudRegelClassificatieSchema,
  type OnderhoudRegelClassificatie,
  type WerkplaatsMatchVia,
  type WerkplaatsSectieDTO,
} from './garage-onderhoud'
import type { VoertuigReminderSoort } from './garage-voertuig'

// ─── Afzenderprofielen (FO-341 §3.1) ─────────────────────────────────────────

export const LEVERANCIER_AFZENDER_PROFIEL_BRON = ['HANDMATIG', 'GELEERD'] as const
export type LeverancierAfzenderProfielBron = (typeof LEVERANCIER_AFZENDER_PROFIEL_BRON)[number]

export const LEVERANCIER_AFZENDER_PROFIEL_BRON_LABELS: Record<LeverancierAfzenderProfielBron, string> = {
  HANDMATIG: 'Ingevoerd',
  GELEERD: 'Geleerd uit de inkoop-review',
}

export const AFZENDER_PROFIEL_SOORT = ['EMAIL', 'DOMEIN'] as const
export type AfzenderProfielSoort = (typeof AFZENDER_PROFIEL_SOORT)[number]

const EMAIL_VORM = /^[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']+$/
const DOMEIN_VORM = /^(?=.{3,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/

/** Kleine letters, zonder spaties en zonder voorloop-`@` (domein). */
export function normaliseerAfzenderWaarde(soort: AfzenderProfielSoort, waarde: string): string {
  const v = waarde.trim().toLowerCase().replace(/\s+/g, '')
  return soort === 'DOMEIN' ? v.replace(/^@+/, '') : v
}

/** Nieuw afzenderprofiel: precies één e-mailadres of één domein. */
export const leverancierAfzenderProfielSchema = z
  .object({
    soort: z.enum(AFZENDER_PROFIEL_SOORT),
    waarde: z.string().trim().min(3, 'Vul een e-mailadres of domein in').max(254, 'Maximaal 254 tekens'),
    notitie: z.string().trim().max(500, 'Maximaal 500 tekens').nullable().optional(),
    /** GELEERD = toegevoegd vanuit de inkoop-review (zelflerend). */
    bron: z.enum(LEVERANCIER_AFZENDER_PROFIEL_BRON).optional(),
    /** Bij GELEERD: de factuur waarop de koppeling werd gemaakt. */
    geleerdVanFactuurId: z.string().min(1).max(64).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    const genorm = normaliseerAfzenderWaarde(v.soort, v.waarde)
    if (v.soort === 'EMAIL' && !EMAIL_VORM.test(genorm)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['waarde'],
        message: 'Geen geldig e-mailadres (bijvoorbeeld facturen@garage.nl).',
      })
    }
    if (v.soort === 'DOMEIN' && !DOMEIN_VORM.test(genorm)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['waarde'],
        message: 'Geen geldig domein (bijvoorbeeld garage.nl, zonder @ en zonder https://).',
      })
    }
  })
export type LeverancierAfzenderProfielInput = z.infer<typeof leverancierAfzenderProfielSchema>

export const leverancierAfzenderProfielUpdateSchema = z.object({
  actief: z.boolean().optional(),
  notitie: z.string().trim().max(500, 'Maximaal 500 tekens').nullable().optional(),
})
export type LeverancierAfzenderProfielUpdateInput = z.infer<typeof leverancierAfzenderProfielUpdateSchema>

export interface LeverancierAfzenderProfielDTO {
  id: string
  leverancierId: string
  soort: AfzenderProfielSoort
  /** Het adres of het domein (kleine letters). */
  waarde: string
  actief: boolean
  bron: LeverancierAfzenderProfielBron
  notitie: string | null
  aangemaaktDoorNaam: string | null
  aangemaaktOp: string
}

// ─── Trefwoordenlijst per garage (FO-341 §3.3) ───────────────────────────────

/**
 * De sleutel waaronder een trefwoord vergeleken wordt: kleine letters, zonder
 * accenten, onzichtbare tekens en leestekens, enkele spaties. "B06 / Onderhoud"
 * en "b06 onderhoud" zijn hetzelfde trefwoord.
 */
export function trefwoordSleutel(tekst: string): string {
  return tekst
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[\u00AD\u200B-\u200F\u2060\uFEFF]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

export const werkplaatsTrefwoordSchema = z
  .object({
    trefwoord: z.string().trim().min(2, 'Minimaal 2 tekens').max(100, 'Maximaal 100 tekens'),
    classificatie: onderhoudRegelClassificatieSchema,
    actief: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    if (trefwoordSleutel(v.trefwoord).length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['trefwoord'],
        message: 'Het trefwoord moet minstens twee letters of cijfers bevatten.',
      })
    }
  })
export type WerkplaatsTrefwoordInput = z.infer<typeof werkplaatsTrefwoordSchema>

export const werkplaatsTrefwoordUpdateSchema = z.object({
  classificatie: onderhoudRegelClassificatieSchema.optional(),
  actief: z.boolean().optional(),
})
export type WerkplaatsTrefwoordUpdateInput = z.infer<typeof werkplaatsTrefwoordUpdateSchema>

export interface WerkplaatsTrefwoordDTO {
  id: string
  leverancierId: string
  trefwoord: string
  classificatie: OnderhoudRegelClassificatie
  actief: boolean
  bewerktOp: string
}

// ─── Interval per merk/model en per voertuig (FO-341 §3.4) ───────────────────

/**
 * Soorten met een interval per merk of per voertuig. De APK hoort er bewust niet
 * bij: die termijn volgt uit de RDW-vervaldatum, nooit uit een eigen interval.
 */
export const MERK_INTERVAL_SOORT = ['KLEINE_BEURT', 'GROTE_BEURT', 'BANDEN'] as const satisfies readonly VoertuigReminderSoort[]
export type MerkIntervalSoort = (typeof MERK_INTERVAL_SOORT)[number]

/** Waar het interval van een voertuig vandaan komt (resolutievolgorde). */
export const INTERVAL_BRON = ['VOERTUIG', 'MERK_MODEL', 'MERK', 'STANDAARD'] as const
export type IntervalBron = (typeof INTERVAL_BRON)[number]

export const INTERVAL_BRON_LABELS: Record<IntervalBron, string> = {
  VOERTUIG: 'Eigen interval van dit voertuig',
  MERK_MODEL: 'Interval van merk en model',
  MERK: 'Interval van het merk',
  STANDAARD: 'Standaardinterval',
}

/** Hint bij het standaardinterval (FO-341 §5). */
export const STANDAARD_INTERVAL_HINT = 'Standaardinterval - stel het merkinterval in.'

const maandenVeld = z
  .number({ message: 'Vul een aantal maanden in' })
  .int('Gebruik een heel aantal maanden')
  .min(1, 'Minimaal 1 maand')
  .max(120, 'Maximaal 120 maanden')
  .nullable()
const kmVeld = z
  .number({ message: 'Vul een aantal km in' })
  .int('Gebruik een heel aantal km')
  .min(500, 'Minimaal 500 km')
  .max(500_000, 'Maximaal 500.000 km')
  .nullable()

function eenVanBeide(v: { maanden: number | null; km: number | null }, ctx: z.RefinementCtx): void {
  if (v.maanden === null && v.km === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['maanden'],
      message: 'Vul een interval in maanden of km in (wat het eerst komt).',
    })
  }
}

/** Merk-/modelrij aanmaken of wijzigen. Merk in hoofdletters zoals de RDW ("MAZDA"). */
export const voertuigMerkIntervalSchema = z
  .object({
    soort: z.enum(MERK_INTERVAL_SOORT),
    merk: z
      .string()
      .trim()
      .min(1, 'Vul het merk in')
      .max(60, 'Maximaal 60 tekens')
      .transform((m) => m.toUpperCase()),
    model: z
      .string()
      .trim()
      .max(60, 'Maximaal 60 tekens')
      .nullable()
      .optional()
      .transform((m) => (m && m.length > 0 ? m : null)),
    maanden: maandenVeld,
    km: kmVeld,
  })
  .superRefine(eenVanBeide)
export type VoertuigMerkIntervalInput = z.infer<typeof voertuigMerkIntervalSchema>

export interface VoertuigMerkIntervalDTO {
  id: string
  soort: MerkIntervalSoort
  soortLabel: string
  merk: string
  model: string | null
  maanden: number | null
  km: number | null
  bewerktOp: string
}

/**
 * De overrides van één voertuig in één keer (PUT): een soort die ontbreekt
 * heeft geen override (meer) en volgt merk of standaard.
 */
export const voertuigIntervalOverridesSchema = z.object({
  overrides: z
    .array(
      z
        .object({
          soort: z.enum(MERK_INTERVAL_SOORT),
          maanden: maandenVeld,
          km: kmVeld,
        })
        .superRefine(eenVanBeide),
    )
    .max(MERK_INTERVAL_SOORT.length, 'Elke soort mag maar één keer voorkomen.')
    .superRefine((lijst, ctx) => {
      const gezien = new Set<string>()
      lijst.forEach((r, i) => {
        if (gezien.has(r.soort)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [i, 'soort'],
            message: 'Elke soort mag maar één keer voorkomen.',
          })
        }
        gezien.add(r.soort)
      })
    }),
})
export type VoertuigIntervalOverridesInput = z.infer<typeof voertuigIntervalOverridesSchema>

/** Het geldende interval per soort voor één voertuig, met de bron. */
export interface VoertuigIntervalResolutieDTO {
  soort: VoertuigReminderSoort
  soortLabel: string
  maanden: number | null
  km: number | null
  bron: IntervalBron
  /** "Interval van het merk MAZDA", "Standaardinterval - stel het merkinterval in." */
  bronOmschrijving: string
  /** De override van dit voertuig (null = geen). */
  override: { maanden: number | null; km: number | null } | null
}

// ─── Terugdraaien (FO-341 §3.4 rollback) ─────────────────────────────────────

export const voertuigOnderhoudTerugdraaienSchema = z.object({
  reden: z
    .string()
    .trim()
    .min(3, 'Geef een reden op (minimaal 3 tekens)')
    .max(500, 'Maximaal 500 tekens'),
  /**
   * Ook de registratie zelf (logregel + voertuigdocument) intrekken. Uit = alleen
   * de intervalreset terugdraaien (bijv. "het was toch geen beurt").
   */
  verwijderRegistratie: z.boolean().optional(),
})
export type VoertuigOnderhoudTerugdraaienInput = z.infer<typeof voertuigOnderhoudTerugdraaienSchema>

export const TERUGDRAAI_UITKOMST = ['TERUGGEDRAAID', 'CONFLICT', 'GEEN_RESET', 'AL_TERUGGEDRAAID'] as const
export type TerugdraaiUitkomst = (typeof TERUGDRAAI_UITKOMST)[number]

export interface TerugdraaiResultaatDTO {
  onderhoudId: string
  uitkomst: TerugdraaiUitkomst
  /** Soorten waarvan het interval hersteld is. */
  hersteld: VoertuigReminderSoort[]
  /** Soorten met een nieuwere reset sinds deze registratie (niet hersteld). */
  conflicten: Array<{ soort: VoertuigReminderSoort; soortLabel: string; nieuwereBasisDatum: string }>
  registratieVerwijderd: boolean
  documentenIngetrokken: number
  melding: string
  /**
   * De gebruiker moet het interval nakijken: een nieuwere uitvoering bleef leidend,
   * of het interval rust op deze registratie zonder vastgelegde stand van ervoor
   * (en geen andere registratie van die dag kan het dragen).
   */
  controleren: boolean
}

// ─── APK-bevestiging door de RDW (FO-341 §3.4) ───────────────────────────────

export const WERKPLAATS_APK_RDW_STATUS = ['VOORLOPIG', 'BEVESTIGD', 'NIET_BIJ_RDW', 'GESTOPT'] as const
export type WerkplaatsApkRdwStatus = (typeof WERKPLAATS_APK_RDW_STATUS)[number]

export const WERKPLAATS_APK_RDW_STATUS_LABELS: Record<WerkplaatsApkRdwStatus, string> = {
  VOORLOPIG: 'Voorlopig: wacht op bevestiging door de RDW',
  BEVESTIGD: 'Bevestigd door de RDW',
  NIET_BIJ_RDW: 'Niet bij de RDW geregistreerd',
  GESTOPT: 'Gestopt (registratie teruggedraaid)',
}

/** Zoveel werkdagen wacht de app op een nieuwere RDW-vervaldatum. */
export const APK_RDW_BEVESTIGING_WERKDAGEN = 5

/**
 * bronModule van de werkplaats-signalen per voertuig (APK niet bij de RDW,
 * service-indicator niet gereset, terugdraaien met conflict); bronEntiteitId =
 * Voertuig.id. Gedeeld door de sluiters en de postvak-reconciliatie.
 */
export const WERKPLAATS_SIGNAAL_BRON_MODULE = 'garage-werkplaats'

/**
 * Zolang een gefactureerde APK voorlopig is, zwijgt het APK-verval-signaal op de
 * achterhaalde datum - maar niet onbeperkt: blijft de RDW onbereikbaar, dan
 * loopt het gewone APK-signaal na zoveel kalenderdagen weer door.
 */
export const APK_VOORLOPIG_ONDERDRUKKING_DAGEN = 14

/** Een late RDW-bevestiging na "niet bij de RDW" telt nog zoveel dagen na de keuring. */
export const APK_RDW_LATE_BEVESTIGING_DAGEN = 90

// ─── Onderhoudsbadge (FO-341 §3.4) ───────────────────────────────────────────

export const ONDERHOUD_BADGE_STATUS = ['OK', 'BINNENKORT', 'ACHTERSTALLIG', 'ONBEKEND'] as const
export type OnderhoudBadgeStatus = (typeof ONDERHOUD_BADGE_STATUS)[number]

export const ONDERHOUD_BADGE_STATUS_LABELS: Record<OnderhoudBadgeStatus, string> = {
  OK: 'Op schema',
  BINNENKORT: 'Binnenkort',
  ACHTERSTALLIG: 'Achterstallig',
  ONBEKEND: 'Onbekend',
}

/** Lijstfilter "onderhoud" op de voertuigenlijst. */
export const VOERTUIG_ONDERHOUD_FILTER = ['BINNENKORT', 'ACHTERSTALLIG', 'AANDACHT'] as const
export type VoertuigOnderhoudFilter = (typeof VOERTUIG_ONDERHOUD_FILTER)[number]

/**
 * De badge op het voertuigdetail, de voertuigenlijst en de portal (Mijn voertuig).
 * Bevat bewust GEEN bedragen: hij gaat ook naar de bestuurder.
 */
export interface VoertuigOnderhoudBadgeDTO {
  /** De laatste beurt (kleine of grote) volgens de reminders. */
  /**
   * `kmGeschat`: de registratie noemde zelf geen km-stand; het km-interval rekent
   * met de laatst bekende stand op die dag (FO-341 §5: "km geschat").
   */
  laatsteBeurt: { datum: string; km: number | null; kmGeschat: boolean } | null
  /** De eerstvolgende beurt: datum en km, wat het eerst komt. */
  volgendeBeurt: {
    soort: VoertuigReminderSoort
    soortLabel: string
    datum: string | null
    km: number | null
    bepalend: 'DATUM' | 'KM' | null
    status: OnderhoudBadgeStatus
    dagenTot: number | null
    kmTot: number | null
  } | null
  interval: { bron: IntervalBron; hint: string | null }
  /**
   * Een recent telematica-rapport (FO-327, ≤ 7 dagen) is voor de weergave
   * leidend; de berekende waarde is dan informatief.
   */
  telematica: { rapportDatum: string; dagenTotOnderhoud: number | null; kmTotOnderhoud: number | null } | null
  apk: {
    /** De APK-vervaldatum van het voertuig (RDW of handmatig), ISO-dag. */
    vervaldatum: string | null
    bron: 'RDW' | 'HANDMATIG' | null
    status: OnderhoudBadgeStatus
    dagenTot: number | null
    /** De laatst gefactureerde APK (werkdatum), of null. */
    laatsteApkDatum: string | null
    rdwStatus: WerkplaatsApkRdwStatus | null
    /** "APK uitgevoerd op 11-05-2026, nieuwe vervaldatum wordt bevestigd door de RDW." */
    melding: string | null
  }
}

/** Compacte versie voor een lijstrij. */
export interface VoertuigOnderhoudLijstBadgeDTO {
  status: OnderhoudBadgeStatus
  volgendeDatum: string | null
  volgendeKm: number | null
  apkVoorlopig: boolean
}

// ─── Inkoop-review: herkenning en vaste termijn (FO-341 §3.1) ─────────────────

export const LEVERANCIER_HERKENNING_METHODE = [
  'AFZENDER_ADRES',
  'AFZENDER_DOMEIN',
  'DEBITEURNUMMER',
  'PDF',
  'AI',
  'CONCEPT',
] as const
export type LeverancierHerkenningMethode = (typeof LEVERANCIER_HERKENNING_METHODE)[number]

/** Hoe de leverancier van een ingelezen factuur gevonden is (review-tekst). */
export interface InkoopLeverancierHerkenningDTO {
  methode: LeverancierHerkenningMethode
  /** Het afzenderadres van de mail (alleen het adres, geen naam), of null. */
  afzender: string | null
  /** "Herkend via afzender facturen@garage.nl" e.d. */
  toelichting: string
  /** Afzender en factuur wezen naar verschillende leveranciers (de PDF won). */
  conflict: string | null
}

/** De vaste inkoop-betaaltermijn van de leverancier, bij de vervaldatum in de review. */
export interface InkoopVasteTermijnDTO {
  dagen: number
  /** De vervaldatum die de PDF noemde (vervangen door de vaste termijn), ISO-dag. */
  pdfVervaldatum: string | null
  /** De vervaldatum staat nog op factuurdatum + termijn (niet handmatig gewijzigd). */
  toegepast: boolean
  /**
   * De huidige leverancier heeft geen vaste termijn, maar de vervaldatum volgt nog
   * de termijn (`dagen`) van de leverancier die de ingest eerst herkende: de review
   * biedt de PDF-datum aan.
   */
  vanVorigeLeverancier?: boolean
}

/**
 * Zelflerend (FO-341 §3.1): de factuur kwam per mail binnen van een adres dat nog
 * niet bij de (nu gekozen) leverancier hoort. De review biedt aan het adres aan
 * de leverancier toe te voegen.
 */
export interface AfzenderLeerVoorstelDTO {
  email: string
  /**
   * null = het domein is niet bruikbaar: een publiek maildomein (gmail e.d.), of
   * er staat al een adres op dat domein bij een andere leverancier. Dan alleen
   * het volledige adres.
   */
  domein: string | null
}

/**
 * "Nee" op het zelflerend voorstel: onthouden per afzender en leverancier, zodat
 * de review het bij de volgende factuur niet opnieuw vraagt.
 */
export const afzenderVoorstelAfwijzenSchema = z.object({
  email: z.string().trim().min(3).max(254),
})
export type AfzenderVoorstelAfwijzenInput = z.infer<typeof afzenderVoorstelAfwijzenSchema>

// ─── Registratie-details op het voertuig (FO-341 §11.4) ──────────────────────

/** Bron-badge van een onderhoudsregistratie (Factuur / Handmatig / Import). */
export const ONDERHOUD_BRON_BADGE_LABELS: Record<'HANDMATIG' | 'GARAGEFACTUUR' | 'IMPORT', string> = {
  HANDMATIG: 'Handmatig',
  GARAGEFACTUUR: 'Factuur',
  IMPORT: 'Import',
}

/**
 * De werkplaats-details van één onderhoudsregistratie (tab Onderhoud): secties
 * (uitklapbaar), de facturen met hun voertuigdocument ("Bekijk factuur" via de
 * same-origin documenten-proxy: alleen bouwstenen documentId + versie), de gele
 * hints, de APK-status bij de RDW en of het interval terug te draaien is.
 * Bedragen in de secties alleen voor wie kosten mag zien (anders null).
 */
export interface WerkplaatsRegistratieDTO {
  onderhoudId: string
  werkorderNummer: string | null
  leverancierNaam: string | null
  facturen: Array<{
    factuurId: string
    factuurNr: string
    rol: 'BRON' | 'DEELFACTUUR' | 'CREDITNOTA'
    documentId: string | null
    versionNr: number | null
    /** Het document is vrijgegeven voor de bestuurder (portal). */
    bestuurderZichtbaar: boolean
  }>
  secties: WerkplaatsSectieDTO[]
  plausibiliteit: string[]
  matchVia: WerkplaatsMatchVia | null
  apkRdwStatus: WerkplaatsApkRdwStatus | null
  reset: {
    /** De registratie zette (een deel van) het interval opnieuw. */
    toegepast: boolean
    soorten: VoertuigReminderSoort[]
    teruggedraaid: boolean
    /**
     * Er staat een vastgelegde stand die terug kan (een eigen reset, een overname
     * van dezelfde dag of de laatste onderhoudsdatum) en hij is nog niet teruggedraaid.
     */
    terugdraaibaar: boolean
  }
}

/** Een werkplaatsfactuur-document vrijgeven (of intrekken) voor de bestuurder. */
export const werkplaatsDocumentVrijgaveSchema = z.object({
  vrijgeven: z.boolean(),
})
export type WerkplaatsDocumentVrijgaveInput = z.infer<typeof werkplaatsDocumentVrijgaveSchema>
